import { InputWithLabel, SelectWithLabel } from '@adotel/components'
import {
  ApolloQueryResult,
  OperationVariables,
  useMutation
} from '@apollo/client'
import {
  Button,
  FormControlLabel,
  Grid,
  MenuItem,
  Modal,
  Paper,
  Radio,
  RadioGroup,
  Slide,
  Switch,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import PolicyIcon from '@material-ui/icons/Policy'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
  ADD_NEW_WEBHOOK_MUTATIONS,
  UPDATE_WEBHOOK_MUTATIONS
} from '../../graphql/channelPush'
import { getDirection, getTranslate } from '../../localization'
import { PushDetails, StateStatus, WebHook } from '../../types/channelPush'
import useStyles from './styles/addNewWebHookModal.styles'

interface AddNewWebHokkModalProps {
  isOpen: boolean
  isEditMode: boolean
  onClose: () => void
  webHookInput: WebHook
  setWebHookInput: React.Dispatch<React.SetStateAction<WebHook>>
  deafaultWebHookInput: WebHook
  webHookMenuItems: {
    label: string
    value: string
  }[]
  refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<
    ApolloQueryResult<{
      channelPush: PushDetails
    }>
  >
}
const AddNewWebHookModal: React.FC<AddNewWebHokkModalProps> = ({
  isOpen,
  isEditMode,
  onClose,
  webHookInput,
  setWebHookInput,
  webHookMenuItems,
  deafaultWebHookInput,
  refetch
}) => {
  const theme = useTheme()
  const classes = useStyles()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const isEndPointValid = (str: string): boolean => {
    return str.startsWith('https://') && str.includes('.') && str.length > 10
  }
  const isIntervalValid = webHookInput.interval >= 300 ? true : false
  const [isEndPointTouched, setIsEndPointTouched] = useState<boolean>(false)
  const [isIntervalTouched, setIsEIntervaTouched] = useState<boolean>(false)
  const { channelCode } = useParams<{ channelCode: string }>()
  const handleChangeForInterval = (e: any) => {
    const num = e.target.value
    setWebHookInput({ ...webHookInput, interval: num })
    setIsEIntervaTouched(true)
  }

  useEffect(() => {
    setIsEndPointTouched(false)
    setIsEIntervaTouched(false)
    setWebHookInput(isEditMode ? webHookInput : deafaultWebHookInput)
  }, [isEditMode, isOpen])

  const [addNewWebHook] = useMutation(ADD_NEW_WEBHOOK_MUTATIONS, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      refetch()
      toast.success(data.addNewWebHook.message)
      onClose()
    },
    onError: (error) => {
      toast.error(error.message)
    }
  })
  const [updateWebHook] = useMutation(UPDATE_WEBHOOK_MUTATIONS, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      refetch()
      toast.success(data.updateWebHook.message)
      onClose()
    },
    onError: (error) => {
      toast.error(error.message)
    }
  })

  const onClicckButton = () => {
    if (isEditMode) {
      updateWebHook({
        variables: {
          input: {
            channelCode: channelCode,
            adaptor: webHookInput.adaptor,
            interval: webHookInput.interval,
            endPoint: webHookInput.endPoint,
            state: webHookInput.state,
            subscribed: webHookInput.subscribed
          }
        }
      })
    } else {
      addNewWebHook({
        variables: {
          input: {
            channelCode: channelCode,
            adaptor: webHookInput.adaptor,
            interval: webHookInput.interval,
            endPoint: webHookInput.endPoint,
            state: webHookInput.state,
            subscribed: webHookInput.subscribed
          }
        }
      })
    }
  }
  return (
    <Modal
      dir={getDirection()}
      open={isOpen}
      onClose={onClose}
      className={classes.modal}>
      <Slide in={isOpen} direction="down">
        <Paper className={classes.paper}>
          <PolicyIcon className={classes.policyIcon} />

          <div className={classes.header}>
            <Typography variant="h3">
              {isEditMode
                ? getTranslate('Edit Web Hook')
                : getTranslate('Add New Web Hook')}
            </Typography>
          </div>

          <div className={classes.inputContainer}>
            <SelectWithLabel
              label={getTranslate('Adaptor')}
              size="small"
              value={webHookInput.adaptor}
              disabled={isEditMode}
              onChange={(e) => {
                setWebHookInput({
                  ...webHookInput,
                  adaptor: e.target.value
                })
              }}>
              {webHookMenuItems.map((item, index) => (
                <MenuItem key={index} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </SelectWithLabel>

            <InputWithLabel
              inputSize="small"
              label={getTranslate('Interval')}
              value={webHookInput.interval}
              type="number"
              onFocus={() => setIsEIntervaTouched(true)}
              onChange={handleChangeForInterval}
              error={isIntervalTouched && !isIntervalValid ? true : false}
              helpertext={
                isIntervalTouched && !isIntervalValid
                  ? getTranslate('Please enter a number bigger than 300 second')
                  : ''
              }
            />
            <InputWithLabel
              inputSize="small"
              label={getTranslate('Endpoint')}
              value={webHookInput.endPoint}
              type="text"
              dir={'ltr'}
              error={
                !isEndPointValid(webHookInput.endPoint) && isEndPointTouched
              }
              helpertext={
                !isEndPointValid(webHookInput.endPoint) && isEndPointTouched
                  ? getTranslate('Please enter a valid end point')
                  : ''
              }
              onBlur={() => setIsEndPointTouched(true)}
              onChange={(e) => {
                setWebHookInput({
                  ...webHookInput,
                  endPoint: e.currentTarget.value
                })
              }}
            />
            <div className={classes.switchContainerActive}>
              <Typography variant="h4">{getTranslate('Active')}</Typography>
              <Typography component="div" className={classes.switch}>
                <Grid
                  component="label"
                  container
                  alignItems="center"
                  justify="flex-start">
                  <Grid item>{getTranslate('No')}</Grid>
                  <Grid item>
                    <Switch
                      name="active"
                      color="primary"
                      onChange={() =>
                        setWebHookInput({
                          ...webHookInput,
                          subscribed: !webHookInput.subscribed
                        })
                      }
                      checked={webHookInput.subscribed}
                      disabled={false}
                    />
                  </Grid>
                  <Grid item>{getTranslate('Yes')}</Grid>
                </Grid>
              </Typography>
            </div>
            <div className={classes.selectStateSelector}>
              <Typography className={classes.selectStateSelectorlabel}>
                {getTranslate('State')}
              </Typography>
              <RadioGroup
                className={classes.stateRadioGroup}
                value={webHookInput.state}
                onChange={(e) =>
                  setWebHookInput({
                    ...webHookInput,
                    state: e.target.value as StateStatus
                  })
                }>
                <FormControlLabel
                  value="Connected"
                  control={<Radio color="primary" />}
                  label={getTranslate('Connected')}
                />
                <FormControlLabel
                  value="Suspended"
                  control={<Radio color="primary" />}
                  label={getTranslate('Suspended')}
                />
                <FormControlLabel
                  value="Disconnected"
                  control={<Radio color="primary" />}
                  label={getTranslate('Disconnected')}
                />
              </RadioGroup>
            </div>
          </div>
          <div className={classes.button}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth={isSmallScreen}
              onClick={() => {
                onClose()
                setIsEndPointTouched(false)
                setIsEIntervaTouched(false)
              }}>
              {getTranslate('Cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              fullWidth={isSmallScreen}
              disabled={
                !isIntervalValid || !isEndPointValid(webHookInput.endPoint)
              }
              onClick={() => {
                onClicckButton()
              }}>
              {isEditMode ? getTranslate('Update') : getTranslate('Save')}
            </Button>
          </div>
        </Paper>
      </Slide>
    </Modal>
  )
}
export default AddNewWebHookModal
